/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { NotAvailable } from '@/components/blocks/NotAvailable';
import { ProductDetailsPromotionShort } from '@/components/blocks/ProductDetails/Aplazo';
import { ProductDetailsBenefitsDisplay } from '@/components/blocks/ProductDetails/Benefits';
import { ProductDetailsBenefitsMobileDisplay } from '@/components/blocks/ProductDetails/BenefitsMobile';
import {
	productDetailsBinaryElementSX,
	productDetailsImageBinaryElementSX,
} from '@/components/blocks/ProductDetails/styles/binaryElement';
import { productDetailsContainerSX } from '@/components/blocks/ProductDetails/styles/container';
import { BundleComboHeader } from '@/components/content/Bundle/BundleComboHeader';
import { BundleAddToCart } from '@/components/content/Bundle/parts/BundleAddToCart';
import { BundleAddToCartBottom } from '@/components/content/Bundle/parts/BundleAddToCartBottom';
import { BundleAttributes } from '@/components/content/Bundle/parts/BundleAttributes';
import { BundleDescriptionDetails } from '@/components/content/Bundle/parts/BundleDescriptionDetails';
import { BundleDetailsDisplay } from '@/components/content/Bundle/parts/BundleDetailsDisplay';
import { BundleGallery } from '@/components/content/Bundle/parts/BundleGallery';
import { BundlePriceContentRecommendation } from '@/components/content/Bundle/parts/BundlePriceContentRecommendation';
import { BundleProductsList } from '@/components/content/Bundle/parts/BundleProductsList';
import { BundlePromoContentRecommendation } from '@/components/content/Bundle/parts/BundlePromoContentRecommendation';
import { SpotBottomRight } from '@/components/content/ProductDetails/parts/SpotBottomRight';
import { useBundleDetailsTable } from '@/data/Content/BundleDetailsTable';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { ContentProvider } from '@/data/context/content';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { ID } from '@/data/types/Basic';
import { Box, Divider, Paper, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, useEffect, useRef, useState } from 'react';

export const Bundle: FC<{
	id: ID;
}> = ({ id }) => {
	const {
		breakpoints,
		dimensions: { contentSpacing },
	} = useTheme();
	const isMobile = useMediaQuery(breakpoints.down('sm'));
	const { storeLocator } = useStoreLocatorState();
	const [physicalStoreName, setPhysicalStoreName] = useState<string>('');
	const bundleDetails = useProductDetails({ partNumber: id.toString() });
	const { product } = bundleDetails;
	const bundleTableData = useBundleDetailsTable({ pdp: bundleDetails, physicalStoreName });
	const { detailsNotAvailable } = useLocalization('productDetail');

	const refButton = useRef<HTMLDivElement>(null);
	const refEnd = useRef<HTMLDivElement>(null);
	const refTop = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setPhysicalStoreName(storeLocator.selectedStore?.physicalStoreName ?? '');
	}, [storeLocator.selectedStore]);

	return product?.partNumber ? (
		<ContentProvider value={{ ...bundleDetails, ...bundleTableData }}>
			<Paper sx={productDetailsContainerSX({ isMobile: false })}>
				<Stack spacing={contentSpacing}>
					<Stack
						direction={{
							md: 'row',
						}}
						spacing={2}
						justifyContent="space-between"
					>
						{isMobile ? (
							<>
								<Box ref={refTop}></Box>
								<BundleGallery product={product} />
								<Box marginY="8px" />
								<BundleDetailsDisplay product={product} category={bundleDetails.category} />
								<BundlePriceContentRecommendation partNumber={product.partNumber} />
								<Box marginTop="11px" />
								<ProductDetailsPromotionShort />
								<BundlePromoContentRecommendation partNumber={product.partNumber} />
								<Divider sx={{ my: '16px' }} />
								<BundleComboHeader />
								<BundleProductsList
									frameSx={{ marginY: '16px' }}
									components={bundleTableData.componentsKUsCleaned}
									selectedSize={bundleTableData.selectedSize}
									onSizeSelect={bundleTableData.onSizeSelect}
								/>
								<BundleAttributes />
								<Box ref={refButton}></Box>
								<BundleAddToCart
									isReadyToAddToCart={bundleTableData.readyToAddToCart}
									onAddToCart={bundleTableData.addToCart}
								/>
								<Divider sx={{ mb: '16px' }} />
								<ProductDetailsBenefitsMobileDisplay />
								<SpotBottomRight />
								<BundleDescriptionDetails product={product} />
								<BundleAddToCartBottom
									refButton={refButton}
									refEnd={refEnd}
									refTop={refTop}
									isReadyToAddToCart={bundleTableData.readyToAddToCart}
									onAddToCart={bundleTableData.addToCart}
								/>
								<Box ref={refEnd}></Box>
							</>
						) : (
							<>
								<Stack spacing={2} sx={productDetailsImageBinaryElementSX}>
									<BundleGallery product={product} />
									<BundleDescriptionDetails product={product} />
								</Stack>
								<Stack spacing={0} sx={productDetailsBinaryElementSX}>
									<BundleDetailsDisplay product={product} category={bundleDetails.category} />
									<BundlePriceContentRecommendation partNumber={product.partNumber} />
									<ProductDetailsPromotionShort />
									<BundlePromoContentRecommendation partNumber={product.partNumber} />
									<Divider sx={{ my: '16px' }} />
									<BundleComboHeader />
									<BundleProductsList
										frameSx={{ marginY: '16px' }}
										components={bundleTableData.componentsKUsCleaned}
										selectedSize={bundleTableData.selectedSize}
										onSizeSelect={bundleTableData.onSizeSelect}
									/>
									<BundleAttributes />
									<BundleAddToCart
										isReadyToAddToCart={bundleTableData.readyToAddToCart}
										onAddToCart={bundleTableData.addToCart}
									/>

									<Divider sx={{ mb: '16px' }} />
									<ProductDetailsBenefitsDisplay />
									<SpotBottomRight />
								</Stack>
							</>
						)}
					</Stack>
				</Stack>
			</Paper>
		</ContentProvider>
	) : (
		<NotAvailable message={detailsNotAvailable.t()} />
	);
};
